import { WebshopUtils } from '../../../shared/webshop/webshop-utils';
import { Observable } from 'rxjs';
import {
  Alert,
  AlertsService,
  ModalService,
} from '@proximus/ng-common-components';
import {
  InceptionService,
  LanguageService,
  TranslationService,
} from '@proximus/ng-common';
import {
  isOptionActive,
  isOptionAvailable,
  Option,
  Product,
} from '@dch/products-business-logic';
import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { EpressConfirmationModalComponent } from '../epress-confirmation-modal/epress-confirmation-modal.component';
import { OptionLockService } from 'src/app/services/option-lock.service';
import {
  buildLockKey,
  isPending,
  retrieveProductAndOptionFromConfirmationEvent,
} from '../../../shared/order-utils/order-utils';
import { OnDestroyHookDirective } from '../../../shared/hooks';
import { buildTaggingProduct } from '../../../tagging/tagging.utils';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[mprd-epress-option]',
  templateUrl: './epress-option.component.html',
  styleUrls: ['./epress-option.component.css'],
})
export class EpressOptionComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  @Input() option: Option;
  @Input() product: Product;
  @Input() ePressChange$: Observable<any>;
  @Input() isEven: boolean;

  @Output() ePressOptionChange = new EventEmitter<any>();

  active: boolean;
  available: boolean;
  clickable: boolean;
  locked: boolean;
  cssClasses: string;
  pending: boolean;
  isAiaPending: boolean;
  private lockedOption: Option;

  constructor(
    private modalService: ModalService,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private optionLockService: OptionLockService,
    private alertService: AlertsService,
    public languageService: LanguageService,
    private inceptionService: InceptionService,
  ) {
    super();
  }

  ngOnInit() {
    this.active = this.option && isOptionActive(this.option);
    this.available = this.option && isOptionAvailable(this.option);
    this.pending = isPending(this.product, this.option, this.isAiaPending);
    this.clickable = this.available && !this.pending;
    this.lockedOption = this.product.options.find(
      (option) =>
        option.remainingDaysBeforeLockExpiry !== undefined &&
        option.remainingDaysBeforeLockExpiry >= 0,
    );
    this.locked = !!this.lockedOption;
    this.determineCssLandingClasses();
    this.ePressChange$.subscribe(() => this.onSwap());
    this.subscribeToWebshopEvents();
  }

  swapPartner() {
    if (!this.pending && !this.isAiaPending) {
      const title = this.locked
        ? this.translationService.getTranslatedValueFor(
            'epress.modal.order-locked.title',
          )
        : this.translationService.getTranslatedValueFor(
            'epress.modal.order.title',
          );
      const confirmButtonLabel = this.locked
        ? this.translationService.getTranslatedValueFor(
            'epress.modal.order-locked.confirm',
          )
        : this.translationService.getTranslatedValueFor(
            'epress.modal.order.confirm',
          );
      const cancelButtonLabel = this.locked
        ? undefined
        : this.translationService.getTranslatedValueFor(
            'epress.modal.order.cancel',
          );
      let description = this.translationService.getTranslatedValueFor(
        'epress.modal.order.description',
      );
      if (this.locked) {
        const remainingDays = this.lockedOption.remainingDaysBeforeLockExpiry
          ? this.lockedOption.remainingDaysBeforeLockExpiry.toString()
          : parseInt(this.lockedOption.lockTime, 10).toString();
        description = this.translationService
          .getTranslatedValueFor('epress.modal.order-locked.description')
          .replace('{0}', remainingDays);
      }
      this.modalService
        .create<EpressConfirmationModalComponent>(
          EpressConfirmationModalComponent,
        )
        .subscribe(
          (componentRef: ComponentRef<EpressConfirmationModalComponent>) => {
            componentRef.instance.title = title;
            componentRef.instance.description = description;
            componentRef.instance.confirmButtonLabel = confirmButtonLabel;
            componentRef.instance.cancelButtonLabel = cancelButtonLabel;
            if (!this.locked) {
              componentRef.instance.confirm = () => this.confirmSwap();
            }
            componentRef.instance.jsonProduct = buildTaggingProduct(
              this.product,
              this.option,
            );
          },
        );
    }
  }

  confirmSwap() {
    WebshopUtils.addProduct(
      false,
      this.option.optionCodeForDescription,
      'RPO',
      this.option.chargeConfiguration.event,
      this.option.chargeConfiguration.code,
      'add',
    );
    this.ePressOptionChange.emit();
  }

  goToEpressSite() {
    window.location.href =
      this.option.configureLink.url[this.languageService.getSelectedLanguage()];
  }

  openUrlOrApp() {
    const desktopFallback =
      this.option.configureLink.url[this.languageService.getSelectedLanguage()];
    const androidApp = this.option.android ? this.option.android.app : '';
    const androidFallback = this.option.android
      ? this.option.android.store
      : '';
    const iosFallback = this.option.ios ? this.option.ios.store : '';
    const iosApp = this.option.ios ? this.option.ios.app : '';
    this.choosePlatform(
      androidApp,
      androidFallback,
      iosApp,
      iosFallback,
      desktopFallback,
    );
  }

  private choosePlatform(
    androidApp: any,
    androidFallback: any,
    iosApp: any,
    iosFallback: any,
    desktopFallback: any,
  ) {
    if (/Android/.test(navigator.userAgent)) {
      this.openAppWindow(androidApp, androidFallback);
    } else if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      this.openAppWindow(iosApp, iosFallback);
    } else {
      window.open(desktopFallback, '_self');
    }
  }

  private openAppWindow(app: any, fallback: any) {
    window.open(app, '_self');
    window.setTimeout(function () {
      window.open(fallback, '_self');
    }, 1000);
  }

  private determineCssLandingClasses() {
    if (this.active) {
      this.cssClasses =
        'rs-bg-green rs-radius rs-padding-mini rs-maxh rs-no-margin jsrs-press-panel0';
    } else if (this.available) {
      this.cssClasses =
        'rs-bg-c2 rs-radius rs-padding-mini rs-maxh rs-no-margin jsrs-press-panel1';
    }
  }

  private determineCssClassesAfterSwap() {
    if (this.active) {
      this.cssClasses =
        'rs-bg-green rs-radius rs-padding-mini rs-maxh rs-no-margin jsrs-press-panel0';
    } else if (this.available) {
      this.cssClasses =
        'rs-bg-c2 rs-radius rs-padding-mini rs-maxh rs-no-margin jsrs-press-panel1';
    }
  }

  private onSwap() {
    this.pending = true;
    this.clickable = false;
    this.locked = this.active;
    this.determineCssClassesAfterSwap();
  }

  private subscribeToWebshopEvents() {
    WebshopUtils.subscribeToCartEvent((data: any) => {
      this.ngZone.run(() => {
        if (data.status === 'PENDING_ORDER') {
          this.isAiaPending = true;
          this.dispatchAlert(
            'warning',
            'message.warning.aiaPending.title',
            'message.warning.aiaPending.description',
          );
        } else if (data.status === 'LOADED') {
          this.isAiaPending = false;
          this.pending = isPending(
            this.product,
            this.option,
            this.isAiaPending,
          );
          if (data.products.length !== 0) {
            this.pending =
              this.pending ||
              data.products.find(
                (p: any) => p.mpcUuid === this.option.optionCodeForDescription,
              ) !== undefined;
          }
          this.clickable = this.available && !this.pending;
        }
      });
    }, this.addSubscription);
    WebshopUtils.subscribeToConfirmationEvent((data: any) => {
      this.ngZone.run(() => {
        if (data.productDetails) {
          const { currentOption } = retrieveProductAndOptionFromConfirmationEvent(
            this.product,
            this.option.optionCodeForDescription,
            data.productDetails,
          );
          if (currentOption) {
            this.lockOption();
            this.ePressOptionChange.emit();
            this.dispatchAlert(
              'success',
              'epress.order.success.title',
              'epress.order.success.description',
            );
          }
        }
      });
    }, this.addSubscription);
    WebshopUtils.subscribeToErrorEvent((data: any) => {
      this.ngZone.run(() => {
        if (
          data.httpCode &&
          data.httpCode === 402 &&
          data.errorCode &&
          data.errorCode === 'shoppingcart.ccw.validation.failed.customer.data'
        ) {
          this.dispatchAlert(
            'error',
            'message.error.title',
            'message.error.description.cwc',
          );
          this.pending = false;
        } else if (data.products) {
          const currentOption = data.products.find(
            (p: any) => p.mpcUuid === this.option.optionCodeForDescription,
          );
          if (currentOption) {
            this.dispatchAlert(
              'error',
              'epress.order.error.title',
              'epress.order.error.description',
            );
            this.pending = false;
          }
        }
      });
    }, this.addSubscription);
  }

  private dispatchAlert(
    type: 'error' | 'info' | 'success' | 'warning',
    title: string,
    description: string,
  ): void {
    const alert: Alert = {
      isClosable: true,
      alertKey: 'swapPartnerAlert',
      title: this.translationService.getTranslatedValueFor(title),
      description: this.translationService.getTranslatedValueFor(description),
      type,
      taggingData: {
        event: 'popup-impression',
        popup: {
          zone: 'e-press',
          name: 'activate success',
        },
        action: {
          name: 'activate',
        },
        product: buildTaggingProduct(this.product, this.option),
      },
    };
    this.alertService.dispatchAlert(alert);
    this.scrollToTop();
  }

  private lockOption() {
    this.optionLockService.lockOption(
      this.option.optionCodeForDescription,
      this.product.plc,
      this.product.accessNumber,
    );
    sessionStorage.setItem(
      buildLockKey(this.product, this.option),
      Date.now().toString(),
    );
  }

  private scrollToTop(): void {
    setTimeout(() => {
      this.inceptionService.scrollToElement('#ePressDetails');
    });
  }
}
